import { useTranslation } from 'react-i18next';
import ReedleMarkdown from '../ReedleMarkdown';
import StreamingMessage from './StreamingMessage';
import { STREAMING_STATES } from '../../hooks/useStreamingStateMachine';
import { parseCitations } from '../../hooks/useCitationParser';
import CitationFooter from './CitationFooter';
import AIChatMessageBubble, {
  AIChatTypingIndicator,
  AIChatSearchingIndicator,
  AIChatError,
} from './AIChatMessageBubble';

/**
 * The RAG transcript. Shared verbatim by the library and deck panels, which
 * previously carried byte-identical copies of this loop; they now differ only in
 * `suggestionContext` and the empty state they pass in.
 *
 * @param {React.ReactNode} props.emptyState - Rendered instead of the loop when there are no messages
 * @param {string} props.suggestionContext - Analytics context for follow-up suggestion clicks
 */
export function RAGMessageList({
  messages,
  emptyState,
  suggestionContext,
  onDelete,
  onSuggestionClick,
  sending,
  searchingArticles,
  error,
  onCitationClick,
  streamingState,
  streamingMessageIndex,
  forceCompleteStreaming,
  streamBufferRef,
  visibleLengthRef,
  handleAnimationComplete,
  onStreamingContentUpdate,
  messagesEndRef,
  containerRef,
}) {
  const { t } = useTranslation('reader');

  return (
    <div className="ai-chat-messages" ref={containerRef}>
      {messages?.length === 0 ? emptyState : (
        messages.map((msg, idx) => {
          // Mirror ChatMessageContent: StreamingMessage stays mounted through
          // NETWORK_COMPLETE while the animation drains the buffer.
          const isCurrentlyStreaming =
            msg.role === 'assistant' &&
            streamingMessageIndex === idx &&
            (streamingState === STREAMING_STATES.NETWORK_STREAMING ||
             streamingState === STREAMING_STATES.NETWORK_COMPLETE);

          const hasSources = msg.role === 'assistant' && msg.sources?.length > 0;
          const citationNumbers = (!isCurrentlyStreaming && hasSources)
            ? parseCitations(msg.content).citationNumbers
            : [];

          return (
            <AIChatMessageBubble
              key={idx}
              message={msg}
              index={idx}
              onDelete={onDelete}
              footer={
                !isCurrentlyStreaming && hasSources && (
                  <CitationFooter
                    sources={msg.sources}
                    citationNumbers={citationNumbers}
                    label={`${t('ragChat.sources.label')}:`}
                  />
                )
              }
            >
              {isCurrentlyStreaming ? (
                <StreamingMessage
                  text={msg.content}
                  isStreaming={streamingState === STREAMING_STATES.NETWORK_STREAMING}
                  forceComplete={forceCompleteStreaming}
                  streamBufferRef={streamBufferRef}
                  visibleLengthRef={visibleLengthRef}
                  onAnimationComplete={() => handleAnimationComplete(idx)}
                  onContentUpdate={onStreamingContentUpdate}
                  enableSuggestions={false}
                />
              ) : (
                <ReedleMarkdown
                  enableSuggestions={msg.role === 'assistant'}
                  onSuggestionClick={onSuggestionClick}
                  suggestionsDisabled={sending}
                  suggestionContext={suggestionContext}
                  citationSources={hasSources ? msg.sources : undefined}
                  onCitationClick={onCitationClick}
                >
                  {msg.content}
                </ReedleMarkdown>
              )}
            </AIChatMessageBubble>
          );
        })
      )}
      {searchingArticles && <AIChatSearchingIndicator />}
      {/* Waiting for the first chunk (streaming) or the whole response (non-streaming) */}
      {sending && !searchingArticles && <AIChatTypingIndicator />}
      <AIChatError error={error} />
      <div ref={messagesEndRef} />
    </div>
  );
}

export default RAGMessageList;
